import React from 'react';
import { ArrowRight, CalendarDays, CheckCircle2, ClipboardCheck, FileInput, FileStack, Printer, QrCode, ScanText, ShieldAlert, Stamp, Upload, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/admin/PageHeader';
import { Panel } from '../../components/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { useDemo } from '../../contexts/DemoContext';
import { approvalStatus, dispatchStatus, divisionProcessing, fraudByCategory, kpis, monthlyProcessing, ocrQuality, recentVerifications, verificationTrend } from '../../data/dashboard';
import { notices } from '../../data/notices';
import { bnNum, toBn } from '../../utils/bangla';
const kpiIcons = [FileStack, Upload, ScanText, ClipboardCheck, Stamp, QrCode, Printer, Users, ShieldAlert];
const kpiColors = ['#003B71', '#1769AA', '#1769AA', '#16834A', '#E69A20', '#006A4E', '#006A4E', '#1769AA', '#D93036'];
const tick = {
  fontSize: 12,
  fill: '#5b6b80',
  fontFamily: 'Hind Siliguri'
};
const bnTick = (v: number) => toBn(v.toLocaleString('en-IN'));
const tooltipFmt = (v: number) => bnNum(v);
export function Dashboard() {
  const navigate = useNavigate();
  const {
    user,
    complaints
  } = useDemo();
  return <div data-id="element-823" data-mp="9gs3147o2cnp">
      <PageHeader flag screen="WEB SCREEN 02" title="ট্রাফিক প্রসিকিউশন মনিটরিং ড্যাশবোর্ড" subtitle={`স্বাগতম, ${user?.name} • সকল সংখ্যা প্রোটোটাইপ প্রদর্শনী তথ্য, প্রকৃত ডিএমপি পরিসংখ্যান নয়`} actions={<>
            <span className="flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm text-slate-600" data-id="element-825" data-mp="jd4xi4b1cuji">
              <CalendarDays className="h-4 w-4" data-id="element-826" data-mp="1utv5nsz0rwfr" /> জানুয়ারি – আগস্ট ২০২৫
            </span>
            <Button onClick={() => navigate('/admin/upload')} data-id="element-827" data-mp="155z7junv1ew2">
              <FileInput className="h-4 w-4" data-id="element-828" data-mp="154eek9h7240b" /> নতুন নোটিশ আপলোড করুন
            </Button>
          </>} data-id="element-824" data-mp="9gi9x7rbrpds" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5" data-id="element-829" data-mp="109ifwqhgcie8">
        {kpis.map((k, i) => {
        const Icon = kpiIcons[i];
        return <div key={k.key} className={`rounded-lg border border-border bg-white p-4 ${i === 0 ? 'xl:row-span-2 xl:flex xl:flex-col xl:justify-between xl:bg-[#003B71] xl:text-white' : ''}`} data-id="element-830" data-mp="1a5v9loczyw8z" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-831" data-mp="hhzjj2hxo2l6">
                <span className={`text-[13px] ${i === 0 ? 'xl:text-white/80' : 'text-slate-600'}`} data-id="element-832" data-mp="w62pwczw1gai">{k.label}</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-md" style={{
              backgroundColor: i === 0 ? 'rgba(255,255,255,0.12)' : `${kpiColors[i]}14`
            }} data-id="element-833" data-mp="rdt1x2ic2qav">
                  <Icon className="h-4 w-4" style={{
                color: i === 0 ? '#fff' : kpiColors[i]
              }} data-id="element-834" data-mp="3uvt584cy27i5" />
                </span>
              </div>
              <div className={`mt-2 font-bold ${i === 0 ? 'text-3xl xl:text-5xl' : 'text-2xl'}`} style={{
            color: i === 0 ? undefined : kpiColors[i]
          }} data-id="element-835" data-mp="2acqowwi7ebo2">
                {bnNum(k.value)}
              </div>
              {i === 0 && <div className="mt-2 text-xs xl:text-white/70" data-id="element-836" data-mp="20h5pn64irpi0">AI প্রসিকিউশন ব্যবস্থা থেকে প্রাপ্ত মোট নোটিশ • প্রক্রিয়াকরণ হার {toBn('৯৪')}%</div>}
            </div>;
      })}
      </div>

      <Panel title="নোটিশ প্রক্রিয়াকরণ কর্মপ্রবাহ সারসংক্ষেপ" className="mt-6" bodyClassName="p-4" data-id="element-837" data-mp="2t4t6qb6kzxzw">
        <div className="flex flex-wrap items-stretch gap-2" data-id="element-838" data-mp="2y2bh8608yla0">
          {[['প্রাপ্ত', 25480], ['স্ক্যান/আপলোড', 23950], ['AI/OCR', 23500], ['যাচাই', 22800], ['অনুমোদন', 22100], ['QR নোটিশ', 22200], ['প্রেরিত', 21500], ['নাগরিক যাচাই', 18750]].map(([label, v], i, arr) => <React.Fragment key={label as string} data-id="element-839" data-mp="1crbfwxfhyh1z" data-mpi={i}>
              <div className="min-w-[110px] flex-1 rounded-md bg-[#F4F7FB] px-3 py-2.5 text-center" data-id="element-840" data-mp="1swmqojzxx749">
                <div className="text-xs text-slate-500" data-id="element-841" data-mp="1o6g38nh5zsnl">{label}</div>
                <div className="text-lg font-bold text-[#003B71]" data-id="element-842" data-mp="1o43d3n196bzg">{bnNum(v as number)}</div>
              </div>
              {i < arr.length - 1 && <ArrowRight className="hidden h-4 w-4 self-center text-slate-300 xl:block" data-id="element-843" data-mp="1e8ihc6ms27y5" />}
            </React.Fragment>)}
        </div>
      </Panel>

      <div className="mt-6 grid gap-6 xl:grid-cols-3" data-id="element-844" data-mp="2bto8j5elf1pm">
        <Panel title="মাসিক প্রসিকিউশন নোটিশ প্রক্রিয়াকরণ" subtitle="প্রাপ্ত, প্রক্রিয়াকৃত ও প্রেরিত" className="xl:col-span-2" data-id="element-845" data-mp="26we7stlr73mh">
          <div className="h-[280px]" data-id="element-846" data-mp="21vrnnlvusseb">
            <ResponsiveContainer data-id="element-847" data-mp="1xw9etohb47ru">
              <AreaChart data={monthlyProcessing} margin={{
              left: -6,
              right: 8,
              top: 8
            }} data-id="element-848" data-mp="1xuy22z5vvpol">
                <CartesianGrid stroke="#eef2f7" vertical={false} data-id="element-849" data-mp="qjh3g3v6e4lh" />
                <XAxis dataKey="month" tick={tick} axisLine={false} tickLine={false} data-id="element-850" data-mp="mgua1yiuwbm6" />
                <YAxis tick={tick} tickFormatter={bnTick} axisLine={false} tickLine={false} data-id="element-851" data-mp="mj6xusbapg75" />
                <Tooltip formatter={tooltipFmt} data-id="element-852" data-mp="2y2cu99lvv3au" />
                <Legend wrapperStyle={{
                fontSize: 12
              }} data-id="element-853" data-mp="326t0jbzescql" />
                <Area type="monotone" dataKey="received" name="প্রাপ্ত" stroke="#003B71" fill="#003B71" fillOpacity={0.08} strokeWidth={2} data-id="element-854" data-mp="374uiftl3m60h" />
                <Area type="monotone" dataKey="processed" name="প্রক্রিয়াকৃত" stroke="#1769AA" fill="#1769AA" fillOpacity={0.08} strokeWidth={2} data-id="element-855" data-mp="3bwm9s6c2wfro" />
                <Area type="monotone" dataKey="dispatched" name="প্রেরিত" stroke="#006A4E" fill="#006A4E" fillOpacity={0.08} strokeWidth={2} data-id="element-856" data-mp="3gx07ovjyu9bz" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="AI/OCR সাফল্য ও পর্যালোচনা সারি" data-id="element-857" data-mp="3uuyjpuatdtob">
          <DonutChart data={ocrQuality} center="৯৩%" centerLabel="গড় নির্ভুলতা" data-id="element-858" data-mp="3uscdpz3knm87" />
        </Panel>

        <Panel title="নোটিশ অনুমোদন অবস্থা" data-id="element-859" data-mp="1vjfcpl92mvha">
          <div className="h-[230px]" data-id="element-860" data-mp="20fw20is0bfvy">
            <ResponsiveContainer data-id="element-861" data-mp="20gxk377fx2n6">
              <BarChart data={approvalStatus} layout="vertical" margin={{
              left: 10,
              right: 20
            }} data-id="element-862" data-mp="33rbi42yjt4b8">
                <XAxis type="number" hide data-id="element-863" data-mp="2ys8cagaquu27" />
                <YAxis type="category" dataKey="name" tick={tick} axisLine={false} tickLine={false} width={80} data-id="element-864" data-mp="2ytjq78xwli71" />
                <Tooltip formatter={tooltipFmt} data-id="element-865" data-mp="3ctkp4iulyfjv" />
                <Bar dataKey="value" name="নোটিশ" radius={[0, 4, 4, 0]} barSize={22} data-id="element-866" data-mp="37t6equ5jatio">
                  {approvalStatus.map((_, i) => <Cell key={i} fill={['#16834A', '#E69A20', '#1769AA', '#D93036'][i]} data-id="element-867" data-mp="3mow5t6pktwu9" data-mpi={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="নোটিশ প্রেরণ অবস্থা" data-id="element-868" data-mp="3vhpntbwerabt">
          <DonutChart data={dispatchStatus} center="৯৭%" centerLabel="প্রেরিত" data-id="element-869" data-mp="3rgdw90tz4f0z" />
        </Panel>
        <Panel title="নাগরিক যাচাই প্রবণতা" subtitle="গত ৭ দিন" data-id="element-870" data-mp="9b14o2i68dzm">
          <div className="h-[230px]" data-id="element-871" data-mp="996mgvyaazz8">
            <ResponsiveContainer data-id="element-872" data-mp="4cpwrxbfsdk1">
              <LineChart data={verificationTrend} margin={{
              left: -14,
              right: 8,
              top: 8
            }} data-id="element-873" data-mp="4dra68fxdc4x">
                <CartesianGrid stroke="#eef2f7" vertical={false} data-id="element-874" data-mp="nzaybsuts83c" />
                <XAxis dataKey="day" tick={tick} axisLine={false} tickLine={false} data-id="element-875" data-mp="sxmaia8leuoq" />
                <YAxis tick={tick} tickFormatter={bnTick} axisLine={false} tickLine={false} data-id="element-876" data-mp="dzttfdnedy8f" />
                <Tooltip formatter={tooltipFmt} data-id="element-877" data-mp="iywoq6dksq37" />
                <Legend wrapperStyle={{
                fontSize: 12
              }} data-id="element-878" data-mp="ixllq8ryfmd5" />
                <Line type="monotone" dataKey="qr" name="QR স্ক্যান" stroke="#006A4E" strokeWidth={2.5} dot={false} data-id="element-879" data-mp="57rwvqgg923f" />
                <Line type="monotone" dataKey="manual" name="ম্যানুয়াল" stroke="#1769AA" strokeWidth={2.5} dot={false} data-id="element-880" data-mp="9ygor6w1k133" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="ধরন অনুযায়ী প্রতারণা রিপোর্ট" actions={<Link to="/admin/fraud" className="text-xs font-medium text-[#1769AA] hover:underline" data-id="element-882" data-mp="62okikm32j1">সব দেখুন</Link>} data-id="element-881" data-mp="9gmnem8spko">
          <DonutChart data={fraudByCategory} center="৮৫" centerLabel="মোট রিপোর্ট" data-id="element-883" data-mp="3mfdjy4wop2js" />
        </Panel>
        <Panel title="ট্রাফিক বিভাগভিত্তিক প্রক্রিয়াকরণ" className="xl:col-span-2" data-id="element-884" data-mp="6pgr1mb6op2r">
          <div className="h-[260px]" data-id="element-885" data-mp="1qnouq1ckkhw">
            <ResponsiveContainer data-id="element-886" data-mp="1rp0owq6tb0o">
              <BarChart data={divisionProcessing} margin={{
              left: -6,
              right: 8,
              top: 8
            }} data-id="element-887" data-mp="1rfd1fsm7fy5">
                <CartesianGrid stroke="#eef2f7" vertical={false} data-id="element-888" data-mp="3sv3xynwo4svn" />
                <XAxis dataKey="division" tick={tick} axisLine={false} tickLine={false} data-id="element-889" data-mp="3nyncy2krmums" />
                <YAxis tick={tick} tickFormatter={bnTick} axisLine={false} tickLine={false} data-id="element-890" data-mp="3jwiix2qenvzm" />
                <Tooltip formatter={tooltipFmt} data-id="element-891" data-mp="3f50gea8qhncj" />
                <Legend wrapperStyle={{
                fontSize: 12
              }} data-id="element-892" data-mp="3f48y2145dm47" />
                <Bar dataKey="processed" name="প্রক্রিয়াকৃত" stackId="a" fill="#003B71" barSize={30} data-id="element-893" data-mp="3a5nxu1owb2fr" />
                <Bar dataKey="pending" name="অপেক্ষমাণ" stackId="a" fill="#E69A20" radius={[4, 4, 0, 0]} data-id="element-894" data-mp="358fbrq0mme98" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3" data-id="element-895" data-mp="2w7qy4wf5afwv">
        <Panel title="সাম্প্রতিক নোটিশ প্রক্রিয়াকরণ" className="xl:col-span-2" bodyClassName="overflow-x-auto" actions={<Link to="/admin/notices" className="text-xs font-medium text-[#1769AA] hover:underline" data-id="element-897" data-mp="1fpyy89hh8nlr">সব নোটিশ</Link>} data-id="element-896" data-mp="1boneulp74ym0">
          <table className="w-full text-sm" data-id="element-898" data-mp="1kp257zh0p3wd">
            <thead className="bg-[#F4F7FB] text-left text-xs text-slate-500" data-id="element-899" data-mp="1kn8vgbb3y3k5">
              <tr data-id="element-900" data-mp="1pnwjqp7yc6ao">
                <th className="px-4 py-2.5 font-medium" data-id="element-901" data-mp="1pmc0vwv02bsn">মামলা নম্বর</th>
                <th className="px-4 py-2.5 font-medium" data-id="element-902" data-mp="s1z5x54iop52">গাড়ির নম্বর</th>
                <th className="px-4 py-2.5 font-medium" data-id="element-903" data-mp="wz7oh6tkuv6u">বিভাগ</th>
                <th className="px-4 py-2.5 font-medium" data-id="element-904" data-mp="wxeh1pb546xs">OCR</th>
                <th className="px-4 py-2.5 font-medium" data-id="element-905" data-mp="11x9ao0mavamj">অনুমোদন</th>
              </tr>
            </thead>
            <tbody data-id="element-906" data-mp="16r53konpi7au">
              {notices.slice(0, 6).map(n => <tr key={n.caseNo} className="border-t border-border hover:bg-[#F4F7FB]/60" data-id="element-907" data-mp="9ay7qjb80xmk">
                  <td className="px-4 py-2.5 font-mono text-[12.5px] text-[#003B71]" data-id="element-908" data-mp="e8hmrlfqtefk">{n.caseNo}</td>
                  <td className="px-4 py-2.5" data-id="element-909" data-mp="3swrrhv2aafjk">{n.vehicle}</td>
                  <td className="px-4 py-2.5" data-id="element-910" data-mp="3sycri07daqot">{n.division}</td>
                  <td className="px-4 py-2.5" data-id="element-911" data-mp="1o46jg7ygucj"><StatusBadge label={n.ocr} data-id="element-912" data-mp="1riauu06jdmm" /></td>
                  <td className="px-4 py-2.5" data-id="element-913" data-mp="6ptkb3mkbm4b"><StatusBadge label={n.approval} data-id="element-914" data-mp="6pjsrblvd6wi" /></td>
                </tr>)}
            </tbody>
          </table>
        </Panel>
        <Panel title="সাম্প্রতিক নাগরিক যাচাই" bodyClassName="divide-y divide-border" data-id="element-915" data-mp="3a5qxllg0ta6v">
          {recentVerifications.map(v => <div key={v.time} className="flex items-center justify-between gap-3 px-5 py-3" data-id="element-916" data-mp="3f5daftrxsof5">
              <div className="min-w-0" data-id="element-917" data-mp="3k2n5pq20buaw">
                <div className="truncate font-mono text-[12.5px] text-slate-800" data-id="element-918" data-mp="3k1lmuzer23im">{v.ref}</div>
                <div className="text-xs text-slate-500" data-id="element-919" data-mp="3owhngny1xz7c">{v.time} • {v.method}</div>
              </div>
              <StatusBadge label={v.result} data-id="element-920" data-mp="2rcwqtesjwodq" />
            </div>)}
        </Panel>
      </div>

      <Panel title="সাম্প্রতিক প্রতারণা অভিযোগ" className="mt-6" bodyClassName="overflow-x-auto" actions={<Button size="sm" variant="outline" onClick={() => navigate('/admin/fraud')} data-id="element-922" data-mp="1dgvc6zu7mmr6">অভিযোগ ব্যবস্থাপনা</Button>} data-id="element-921" data-mp="2wbzoge67bsbo">
        <table className="w-full text-sm" data-id="element-923" data-mp="14ib0wjqzbfcz">
          <thead className="bg-[#F4F7FB] text-left text-xs text-slate-500" data-id="element-924" data-mp="14h8989lu18w0">
            <tr data-id="element-925" data-mp="zjq28ordl3qn">
              <th className="px-4 py-2.5 font-medium" data-id="element-926" data-mp="zi58h1cvxotu">অভিযোগ ID</th>
              <th className="px-4 py-2.5 font-medium" data-id="element-927" data-mp="ujvc4nffci6n">ধরন</th>
              <th className="px-4 py-2.5 font-medium" data-id="element-928" data-mp="uleucjkn53b2">গাড়ির নম্বর</th>
              <th className="px-4 py-2.5 font-medium" data-id="element-929" data-mp="pslmhmtvleur">তারিখ</th>
              <th className="px-4 py-2.5 font-medium" data-id="element-930" data-mp="lrjw7vhxsd4x">অবস্থা</th>
            </tr>
          </thead>
          <tbody data-id="element-931" data-mp="gsyxk0ic21il">
            {complaints.slice(0, 4).map(c => <tr key={c.id} onClick={() => navigate(`/admin/fraud?id=${c.id}`)} className="cursor-pointer border-t border-border hover:bg-[#F4F7FB]/60" data-id="element-932" data-mp="gqm68wqoqesv">
                <td className="px-4 py-2.5 font-mono text-[12.5px] text-[#003B71]" data-id="element-933" data-mp="6vkfxxkob6op">{c.id}</td>
                <td className="px-4 py-2.5" data-id="element-934" data-mp="3tc1jwlutset7">{c.type}</td>
                <td className="px-4 py-2.5" data-id="element-935" data-mp="3tenrglhpuubf">{c.vehicle}</td>
                <td className="px-4 py-2.5" data-id="element-936" data-mp="26t3wh0gim9v">{c.date}</td>
                <td className="px-4 py-2.5" data-id="element-937" data-mp="3kbwcwctjnifk"><StatusBadge label={c.status} data-id="element-938" data-mp="3kcob7veizu9j" /></td>
              </tr>)}
          </tbody>
        </table>
      </Panel>
      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-939" data-mp="3odhzipvvrb2y">
        <CheckCircle2 className="h-3.5 w-3.5" data-id="element-940" data-mp="fyptoeenkk33" /> ড্যাশবোর্ডের সকল পরিসংখ্যান প্রোটোটাইপ প্রদর্শনের জন্য তৈরি নমুনা তথ্য।
      </p>
    </div>;
}
function DonutChart({
  data,
  center,
  centerLabel
}: {
  data: {
    name: string;
    value: number;
    color: string;
  }[];
  center: string;
  centerLabel: string;
}) {
  return <div data-id="element-941" data-mp="b5df6x3otto2">
      <div className="relative h-[170px]" data-id="element-942" data-mp="b4vd3jucgl88">
        <ResponsiveContainer data-id="element-943" data-mp="ywivfddjinyw">
          <PieChart data-id="element-944" data-mp="yv7s1eo0bi8b">
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none" data-id="element-945" data-mp="yuzgbq3meiyp">
              {data.map(d => <Cell key={d.name} fill={d.color} data-id="element-946" data-mp="2ocmlk185xc1x" />)}
            </Pie>
            <Tooltip formatter={tooltipFmt} data-id="element-947" data-mp="2jel4tmy7lotm" />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" data-id="element-948" data-mp="2y2xzmglk91mj">
          <div className="text-xl font-bold text-[#003B71]" data-id="element-949" data-mp="2t3l3f58sui7a">{center}</div>
          <div className="text-[11px] text-slate-500" data-id="element-950" data-mp="2t6h2wy0apexu">{centerLabel}</div>
        </div>
      </div>
      <ul className="mt-2 space-y-1.5" data-id="element-951" data-mp="25gd91icwstk1">
        {data.map(d => <li key={d.name} className="flex items-center justify-between text-[13px]" data-id="element-952" data-mp="20h0cu705ea4s">
            <span className="flex items-center gap-2" data-id="element-953" data-mp="2fdhl2pd1j8n4"><span className="h-2.5 w-2.5 rounded-sm" style={{
            backgroundColor: d.color
          }} data-id="element-954" data-mp="2fadczgwgfsr6" />{d.name}</span>
            <span className="font-semibold" data-id="element-955" data-mp="3qrv51j4ob3xv">{bnNum(d.value)}</span>
          </li>)}
      </ul>
    </div>;
}